import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_PREFIX = "nexus.dashboard.recentAccounts";
const MAX_RECENT = 5;

export interface Account {
  id: string;
  name: string;
}

function storageKey(tenantId: string | null): string | null {
  return tenantId ? `${STORAGE_PREFIX}.${tenantId}` : null;
}

function readStorage(key: string | null): string[] {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeStorage(key: string | null, ids: string[]): void {
  if (!key || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // swallow — quota/private-mode errors shouldn't break the dropdown
  }
}

export interface UseRecentAccountsResult {
  /** Recent accounts (most recent first). */
  recent: Account[];
  /** Record an account as recently selected. */
  record: (accountId: string) => void;
}

/**
 * Track recently selected accounts in `localStorage`, scoped per tenant.
 * Prunes IDs that are no longer in the account list.
 */
export function useRecentAccounts(
  accounts: Account[],
  tenantId: string | null,
): UseRecentAccountsResult {
  const [ids, setIds] = useState<string[]>(() => readStorage(storageKey(tenantId)));

  // Re-read from the new tenant-scoped key when the active tenant changes.
  // Track the previous tenantId to skip the no-op case on first render.
  const prevTenantIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevTenantIdRef.current === tenantId) return;
    prevTenantIdRef.current = tenantId;
    setIds(readStorage(storageKey(tenantId)));
  }, [tenantId]);

  const validAccountIds = useMemo(
    () => new Set(accounts.map((a) => a.id)),
    [accounts],
  );

  // Prune IDs that no longer exist in this tenant's account list.
  // Uses functional updater so `ids` is not in the dep array (avoids an infinite loop).
  // Guards the localStorage write so background account-list refetches don't cause churn.
  useEffect(() => {
    setIds((prev) => {
      const pruned = prev.filter((id) => validAccountIds.has(id));
      if (pruned.length === prev.length) return prev;
      writeStorage(storageKey(tenantId), pruned);
      return pruned;
    });
  }, [validAccountIds, tenantId]);

  const record = useCallback(
    (accountId: string) => {
      if (!tenantId) return;
      setIds((prev) => {
        const next = [accountId, ...prev.filter((id) => id !== accountId)].slice(0, MAX_RECENT);
        writeStorage(storageKey(tenantId), next);
        return next;
      });
    },
    [tenantId],
  );

  const recent = useMemo(() => {
    const byId = new Map(accounts.map((a) => [a.id, a]));
    return ids.map((id) => byId.get(id)).filter((a): a is Account => Boolean(a));
  }, [ids, accounts]);

  return { recent, record };
}
